import { AxiosResponse } from "axios";
import { createAxiosInstance } from "./AxiosService";
import { getCurrentUserId, isAdmin } from "./AuthService";

const axiosInstance = createAxiosInstance();

export interface UserContextRole {
  id: string;
  user_id: string;
  role_context_id: string;
  context: {
    id: string;
    name: string;
    description?: string;
    created_at: string;
    updated_at: string;
  };
  role: {
    id: string;
    name: string;
    description?: string;
    created_at: string;
    updated_at: string;
  };
  created_at: string;
  updated_at: string;
}

export interface CreateUserContextRoleRequest {
  user_id: string;
  role_context_id: string;
}

export interface PaginationParams {
  page?: number;
  per_page?: number;
  search?: string;
}

export interface PaginatedResponse<T> {
  user_context_roles: T[];
  pagination: {
    page: number;
    per_page: number;
    total: number;
    pages: number;
  };
}

export class UserContextRoleService {
  // Create a new user context role (admin only)
  static async createUserContextRole(
    roleData: CreateUserContextRoleRequest,
  ): Promise<UserContextRole> {
    try {
      const response: AxiosResponse<UserContextRole> = await axiosInstance.post(
        "/user/user-context-roles/",
        roleData,
      );
      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.error || "Failed to create user context role",
      );
    }
  }

  // Get user context role by ID (admin or own data)
  static async getUserContextRoleById(
    roleId: string,
  ): Promise<UserContextRole> {
    try {
      const response: AxiosResponse<UserContextRole> = await axiosInstance.get(
        `/user/user-context-roles/${roleId}`,
      );
      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.error || "Failed to fetch user context role",
      );
    }
  }

  // Get user context roles by user ID (admin or own data)
  static async getUserContextRolesByUser(
    userId: string,
    params: PaginationParams = {},
  ): Promise<PaginatedResponse<UserContextRole>> {
    try {
      const response: AxiosResponse<PaginatedResponse<UserContextRole>> =
        await axiosInstance.get(`/user/user-context-roles/by-user/${userId}`, {
          params,
        });
      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.error ||
          "Failed to fetch user context roles by user",
      );
    }
  }

  // Get all user context roles (admin only)
  static async getAllUserContextRoles(
    params: PaginationParams = {},
  ): Promise<PaginatedResponse<UserContextRole>> {
    try {
      const response: AxiosResponse<PaginatedResponse<UserContextRole>> =
        await axiosInstance.get("/user/user-context-roles/", {
          params,
        });
      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.error || "Failed to fetch user context roles",
      );
    }
  }

  // Update user context role (admin only)
  static async updateUserContextRole(
    roleId: string,
    roleData: Partial<CreateUserContextRoleRequest>,
  ): Promise<UserContextRole> {
    try {
      const response: AxiosResponse<UserContextRole> = await axiosInstance.put(
        `/user/user-context-roles/${roleId}`,
        roleData,
      );
      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.error || "Failed to update user context role",
      );
    }
  }

  // Delete user context role (admin only)
  static async deleteUserContextRole(roleId: string): Promise<void> {
    try {
      await axiosInstance.delete(`/user/user-context-roles/${roleId}`);
    } catch (error: any) {
      throw new Error(
        error.response?.data?.error || "Failed to delete user context role",
      );
    }
  }

  // Get current user's context roles
  static async getCurrentUserContextRoles(
    params: PaginationParams = {},
  ): Promise<PaginatedResponse<UserContextRole>> {
    const currentUserId = getCurrentUserId();
    if (!currentUserId) {
      throw new Error("No current user found");
    }
    return this.getUserContextRolesByUser(currentUserId, params);
  }

  // Check if current user can access specific user context role data
  static canAccessUserContextRole(userId: string): boolean {
    const currentUserId = getCurrentUserId();
    return currentUserId === userId || isAdmin();
  }

  // Utility methods for permission checking
  static async userHasPermission(
    userId: string,
    contextName: string,
    roleName: string,
  ): Promise<boolean> {
    try {
      const roles = await this.getUserContextRolesByUser(userId);
      return roles.user_context_roles.some(
        (ucr) => ucr.context.name === contextName && ucr.role.name === roleName,
      );
    } catch (error) {
      console.error("Failed to check user permission:", error);
      return false;
    }
  }

  static async currentUserHasPermission(
    contextName: string,
    roleName: string,
  ): Promise<boolean> {
    const currentUserId = getCurrentUserId();
    if (!currentUserId) return false;
    return this.userHasPermission(currentUserId, contextName, roleName);
  }

  // Get user permissions by context
  static async getUserPermissionsByContext(
    userId: string,
    contextName: string,
  ): Promise<UserContextRole[]> {
    try {
      const roles = await this.getUserContextRolesByUser(userId);
      return roles.user_context_roles.filter(
        (ucr) => ucr.context.name === contextName,
      );
    } catch (error: any) {
      throw new Error(
        error.response?.data?.error ||
          "Failed to fetch user permissions by context",
      );
    }
  }

  // Get all contexts for a user
  static async getUserContexts(userId: string): Promise<string[]> {
    try {
      const roles = await this.getUserContextRolesByUser(userId);
      const contexts = new Set<string>();
      roles.user_context_roles.forEach((ucr) => {
        contexts.add(ucr.context.name);
      });
      return Array.from(contexts);
    } catch (error: any) {
      throw new Error(
        error.response?.data?.error || "Failed to fetch user contexts",
      );
    }
  }

  // Bulk operations
  static async assignMultipleRoles(
    userId: string,
    roleContextIds: string[],
  ): Promise<UserContextRole[]> {
    try {
      const promises = roleContextIds.map((roleContextId) =>
        this.createUserContextRole({
          user_id: userId,
          role_context_id: roleContextId,
        }),
      );
      return await Promise.all(promises);
    } catch (error: any) {
      throw new Error(
        error.response?.data?.error || "Failed to assign multiple roles",
      );
    }
  }

  static async removeMultipleRoles(roleIds: string[]): Promise<void> {
    try {
      const promises = roleIds.map((roleId) =>
        this.deleteUserContextRole(roleId),
      );
      await Promise.all(promises);
    } catch (error: any) {
      throw new Error(
        error.response?.data?.error || "Failed to remove multiple roles",
      );
    }
  }

  // Check if service is available (requires admin access for most operations)
  static isAvailable(): boolean {
    return true; // Basic availability, actual permissions checked per operation
  }

  static isAdminRequired(): boolean {
    return true; // Most operations require admin access
  }

  // Get user context roles by context
  static async getUserContextRolesByContext(
    contextId: string,
    params: PaginationParams = {},
  ): Promise<PaginatedResponse<UserContextRole>> {
    try {
      const response: AxiosResponse<PaginatedResponse<UserContextRole>> =
        await axiosInstance.get(
          `/user/user-context-roles/by-context/${contextId}`,
          {
            params,
          },
        );
      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.error ||
          "Failed to fetch user context roles by context",
      );
    }
  }

  // Get user context roles by role
  static async getUserContextRolesByRole(
    roleId: string,
    params: PaginationParams = {},
  ): Promise<PaginatedResponse<UserContextRole>> {
    try {
      const response: AxiosResponse<PaginatedResponse<UserContextRole>> =
        await axiosInstance.get(`/user/user-context-roles/by-role/${roleId}`, {
          params,
        });
      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.error ||
          "Failed to fetch user context roles by role",
      );
    }
  }

  // Export for backward compatibility
  static async getCurrentUser(): Promise<UserContextRole[]> {
    const currentUserId = getCurrentUserId();
    if (!currentUserId) {
      throw new Error("No current user");
    }
    const result = await this.getUserContextRolesByUser(currentUserId);
    return result.user_context_roles;
  }
}

export default UserContextRoleService;
